import { getPoisByCity } from './poiDatabase';
import { dayTemplatesByCity } from './dayTemplatesByCity';

// This function maps POI data to the format expected by the Discover component
const mapPoiToActivity = (poi, city) => {
  // Check for images in both poi.images and poi.imageUrl
  const imageSource = (poi.images && poi.images.length > 0) 
    ? poi.images[0] 
    : (poi.imageUrl || `https://via.placeholder.com/400x300?text=${encodeURIComponent(poi.title)}`);
    
  return {
    id: poi.id || poi.title.toLowerCase().replace(/\s+/g, '-'),
    title: poi.title,
    desc: poi.desc || poi.description || "No description available",
    lat: poi.lat,
    lng: poi.lng,
    imageUrl: imageSource,
    // Include the full images array if it exists
    ...(poi.images && { images: poi.images }),
    website: poi.website || `https://www.google.com/search?q=${encodeURIComponent(poi.title + ' ' + city)}`,
    rating: poi.rating || 4.0, // Default rating for template POIs
    // Include other POI properties that might be useful
    ...(poi.hours && { hours: poi.hours }),
    ...(poi.price && { price: poi.price }),
    ...(poi.tips && { tips: poi.tips }),
    ...(poi.phone && { phone: poi.phone })
  };
};

// Helper to extract POIs from day templates
const getPoisFromTemplates = (city) => {
  const templates = dayTemplatesByCity[city] || [];
  const pois = [];
  const seen = new Set();

  console.log(`Extracting POIs for ${city} from ${templates.length} templates`);
  templates.forEach((template, index) => {
    console.log(`  Processing template ${index + 1}: ${template.label}`);
    Object.values(template.slots).forEach((slot, slotIndex) => {
      if (Array.isArray(slot)) {
        slot.forEach((activity, activityIndex) => {
          if (activity.title) {
            if (!seen.has(activity.title)) {
              seen.add(activity.title);
              pois.push({
                ...activity,
                city: activity.city || city
              });
              console.log(`    Added: ${activity.title}`);
            } else {
              console.log(`    Skipped (duplicate): ${activity.title}`);
            }
          }
        });
      }
    });
  });
  console.log(`Extracted ${pois.length} unique POIs for ${city}`);

  return pois;
};

// List of supported cities to show in Discover tab
const CITIES = [
  'Lisbon',
  'Porto',
  // Hidden from Discover tab:
  // 'Faro',
  'Coimbra',
  'Sintra',
  'Évora',
  'Braga',
  'Aveiro',
  'Albufeira',
  'Lagos',
  'Sagres',
  'Douro',
  // Hidden from Discover tab:
  // 'Additional'
].filter(Boolean); // Remove any empty entries

// Generate the activitiesByCity object dynamically
const activitiesByCity = CITIES.reduce((acc, city) => {
  // Get POIs from the database
  const dbPois = getPoisByCity(city);
  
  // Get POIs from day templates
  const templatePois = getPoisFromTemplates(city);
  
  // Combine POIs, preferring template POIs with images over database entries
  const allPois = [];
  const seenTitles = new Set();
  
  // First add all template POIs that have images
  templatePois.forEach(poi => {
    if (poi.images && poi.images.length > 0) {
      allPois.push(poi);
      seenTitles.add(poi.title);
    }
  });
  
  // Then add database POIs that weren't already added from templates
  dbPois.forEach(poi => {
    if (!seenTitles.has(poi.title)) {
      allPois.push(poi);
      seenTitles.add(poi.title);
    }
  });
  
  // Finally, add any remaining template POIs that don't have images
  templatePois.forEach(poi => {
    if (!seenTitles.has(poi.title)) {
      allPois.push(poi);
      seenTitles.add(poi.title);
    }
  });
  
  console.log(`Found ${allPois.length} POIs for ${city} (${dbPois.length} from DB, ${templatePois.length} from templates)`);
  
  // Map to the format expected by the Discover component and filter out duplicates and POIs without images
  acc[city] = allPois
    .map(poi => mapPoiToActivity(poi, city))
    .filter(poi => {
      // List of POIs to hide from Discover tab (duplicates and specific POIs)
      const hiddenPois = [
        'Crystal Palace Gardens (Jardins do Palácio de Cristal)',
        'Crystal Palace Gardens',
        'Gaia Cable Car',
        'Clérigos Tower',
        'Ribeira Square',
        'Miradouro de São Leonardo de Galafura',
        'Breathtaking',
        'Quinta Nova de Nossa Senhora do Carmo',
        'Praia do Beliche',
        'Praia do Martinhal',
        'Praia do Tonel',
        'Meia Praia',
        'Lagos Zoo',
        'Fort Ponta da Bandeira',
        'Albufeira Marina',
        'The Strip',
        'São Sebastião Church',
        'Praia dos Pescadores',
        'Brasão Coliseu',
        'Cantinho do Avilez',
        'O Gaveto'
      ];
      
      // Only include POIs that have at least one image
      const hasImage = (poi.images && poi.images.length > 0) || 
                     (poi.imageUrl && !poi.imageUrl.includes('placeholder.com'));
      
      return !hiddenPois.includes(poi.title) && hasImage;
    });
  
  return acc;
}, {});

console.log('Final activitiesByCity:', activitiesByCity);
export { activitiesByCity };
